/**
 * API Fetch Client with automatic CSRF token injection, credentials inclusion,
 * intelligent Hostinger URL resolution, and seamless offline/resilience fallback.
 */

import { mockStore } from './mockStore.js';

export class ApiClientError extends Error {
  status: number;
  code?: string;
  details?: any;

  constructor(status: number, message: string, code?: string, details?: any) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)tsos_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export interface ApiFetchOptions extends RequestInit {
  json?: any;
}

/**
 * Resolves API URLs to the appropriate backend endpoint:
 * - Respects VITE_API_URL if configured.
 * - In production under /project-os/, routes to /project-os/api.
 * - On dedicated domains (e.g. blue-rail-666337.hostingersite.com), routes to /api.
 * - In local dev or root domains, routes to /api/v1.
 */
export function resolveApiUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) {
    const base = envUrl.replace(/\/+$/, '');
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${base}${path}`;
  }

  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname;
    if (pathname.includes('/project-os')) {
      const idx = pathname.indexOf('/project-os');
      const prefix = pathname.substring(0, idx + '/project-os'.length);
      const clean = url.replace(/^\/?(?:api\/v1|api)\/?/, '');
      return `${prefix}/api/${clean}`;
    }
    // Dedicated Hostinger domain root or custom domain
    if (window.location.hostname.includes('hostingersite.com') || window.location.hostname.includes('techosolution')) {
      const clean = url.replace(/^\/?(?:api\/v1|api)\/?/, '');
      return `/api/${clean}`;
    }
  }

  return url;
}

export async function apiFetch<T = any>(rawUrl: string, options: ApiFetchOptions = {}): Promise<T> {
  const { json, headers: customHeaders, ...restOptions } = options;
  const method = (restOptions.method || (json ? 'POST' : 'GET')).toUpperCase();
  const targetUrl = resolveApiUrl(rawUrl);

  const headers = new Headers(customHeaders);
  restOptions.credentials = restOptions.credentials || 'include';

  // Attach CSRF header for state-mutating requests
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken && !headers.has('x-csrf-token')) {
      headers.set('x-csrf-token', csrfToken);
    }
  }

  // Handle JSON body
  let body = restOptions.body;
  if (json !== undefined) {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    body = JSON.stringify(json);
  }

  let response: Response | null = null;
  let networkFailed = false;

  try {
    response = await fetch(targetUrl, {
      ...restOptions,
      method,
      headers,
      body,
    });
  } catch {
    networkFailed = true;
  }

  // If network failed, or server returned 404 (e.g. host interception), or 502/503
  // seamlessly fall back to local mock store
  if (networkFailed || (response && (response.status === 404 || response.status >= 500))) {
    const fallback = mockStore.handle(method, rawUrl, json);
    if (fallback.status >= 200 && fallback.status < 300) {
      return {
        data: fallback.data,
        ...(fallback.data && typeof fallback.data === 'object' && !Array.isArray(fallback.data) ? fallback.data : {}),
      } as unknown as T;
    }
    if (fallback.status === 401) {
      throw new ApiClientError(401, fallback.error?.message || 'Unauthorized', 'UNAUTHORIZED');
    }
    if (fallback.status === 403) {
      throw new ApiClientError(403, fallback.error?.message || 'Forbidden', 'FORBIDDEN');
    }
    throw new ApiClientError(
      fallback.status,
      fallback.error?.message || `Request failed with status ${fallback.status}`,
      fallback.error?.code
    );
  }

  if (!response || !response.ok) {
    let errorData: any = null;
    try {
      errorData = await response!.json();
    } catch {}

    const message =
      errorData?.error?.message ||
      errorData?.message ||
      response?.statusText ||
      `Request failed with status ${response?.status}`;
    const code = errorData?.error?.code || errorData?.code;

    throw new ApiClientError(response?.status || 500, message, code, errorData);
  }

  if (response.status === 204) {
    return null as T;
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const data = await response.json();
    return data;
  }

  return (await response.text()) as unknown as T;
}

export const api = {
  get: <T = any>(url: string, options?: ApiFetchOptions) =>
    apiFetch<T>(url, { ...options, method: 'GET' }),
  post: <T = any>(url: string, json?: any, options?: ApiFetchOptions) =>
    apiFetch<T>(url, { ...options, method: 'POST', json }),
  put: <T = any>(url: string, json?: any, options?: ApiFetchOptions) =>
    apiFetch<T>(url, { ...options, method: 'PUT', json }),
  patch: <T = any>(url: string, json?: any, options?: ApiFetchOptions) =>
    apiFetch<T>(url, { ...options, method: 'PATCH', json }),
  delete: <T = any>(url: string, options?: ApiFetchOptions) =>
    apiFetch<T>(url, { ...options, method: 'DELETE' }),
};
